/**
 * Shared date/time utilities.
 *
 * The backend stores all timestamps in UTC.  When the JSON value
 * lacks a trailing "Z" (or "+00:00"), JavaScript's `new Date()`
 * treats it as *local* time, which shifts the displayed value by
 * the user's timezone offset.
 *
 * `parseUTC` normalises the raw string so the resulting Date
 * object always represents the correct UTC instant.  UI helpers
 * like `toLocaleString` then convert it to the user's local time
 * automatically.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * THE TWO KINDS OF DATE, AND WHY THEY ARE NOT THE SAME KIND
 *
 * 1. A TIMESTAMP records WHEN SOMETHING HAPPENED. It is an instant. It is stored in UTC and is
 *    rendered through `parseUTC` + a `toLocale*` call, which converts it to the reader's clock.
 *    `created_at`, `submitted_at`, `locked_at`.
 *
 * 2. A CALENDAR DAY is a HUMAN ASSERTION: "this is the day the work happened". It is not an
 *    instant, it has no time zone of its own, and it must be the day THE PERSON TYPING IT was
 *    living in. `report_date`, `entry_date`, `ticket_date`, `effective_date`.
 *
 * `new Date().toISOString().slice(0, 10)` answers neither question. It gives the UTC calendar day,
 * which in US Central is already TOMORROW from about 7pm. A foreman filing "today's" work at the
 * end of the day got tomorrow's date, and that date decides which payroll week his hours land in.
 * `todayISO()` below is the answer for case 2; never reach for `toISOString()` to get a day.
 *
 * A FIXED OFFSET IS NOT A FIX. This application is multi-tenant and companies are not all in
 * Central; the browser's own local calendar is the only thing that knows the typist's day.
 */

/**
 * The local calendar day of a Date, as `YYYY-MM-DD`.
 *
 * Deliberately built from `getFullYear`/`getMonth`/`getDate` — the LOCAL accessors — rather than
 * `toISOString()`, so the answer is the day showing on the user's own wall calendar.
 */
export const toLocalISODate = (d = new Date()) => {
  const dt = d instanceof Date ? d : new Date(d)
  if (isNaN(dt.getTime())) return ''
  const year = dt.getFullYear()
  const month = String(dt.getMonth() + 1).padStart(2, '0')
  const day = String(dt.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Today, on the user's own calendar, as `YYYY-MM-DD`.
 *
 * Call this at the moment you need it rather than hoisting it to a module constant: a tablet left
 * open on a job site overnight keeps a module constant from yesterday.
 */
export const todayISO = () => toLocalISODate(new Date())

/**
 * A Date as the value an `<input type="datetime-local">` wants: `YYYY-MM-DDTHH:MM`, local.
 *
 * The input is called datetime-LOCAL and the browser reads and writes it in the user's own zone,
 * so `toISOString().slice(0, 16)` puts the UTC wall clock into a box labelled with local time —
 * a call logged at 20:12 Central showed as 01:12 the next morning.
 */
export const toLocalDateTimeValue = (d = new Date()) => {
  const dt = d instanceof Date ? d : new Date(d)
  if (isNaN(dt.getTime())) return ''
  const hh = String(dt.getHours()).padStart(2, '0')
  const mm = String(dt.getMinutes()).padStart(2, '0')
  return `${toLocalISODate(dt)}T${hh}:${mm}`
}

/**
 * The local calendar month of a Date, as `YYYY-MM`. Same reasoning as `toLocalISODate`.
 */
export const toLocalISOMonth = (d = new Date()) => {
  const iso = toLocalISODate(d)
  return iso ? iso.slice(0, 7) : ''
}

/**
 * Monday of the week containing `d`, as a local Date at LOCAL MIDNIGHT.
 *
 * The midnight matters. The obvious version keeps the time of day, and callers then formatted it
 * with `toISOString()`, so a week looked up at 8pm Central started on TUESDAY — the whole payroll
 * week shifted by one day, which is worse than one report being misdated. Returning midnight makes
 * the value a calendar day rather than an instant, and `toLocalISODate` is the only correct way to
 * write it down.
 */
export const startOfWeekLocal = (d = new Date()) => {
  const dt = d instanceof Date ? new Date(d.getTime()) : new Date(d)
  if (isNaN(dt.getTime())) return new Date(NaN)
  dt.setHours(0, 0, 0, 0)
  const day = dt.getDay()                    // 0 = Sunday
  dt.setDate(dt.getDate() + (day === 0 ? -6 : 1 - day))
  return dt
}

/**
 * `n` days after a local Date, at local midnight. Handles DST because it goes through `setDate`.
 */
export const addDaysLocal = (d, n) => {
  const dt = d instanceof Date ? new Date(d.getTime()) : new Date(d)
  if (isNaN(dt.getTime())) return new Date(NaN)
  dt.setHours(0, 0, 0, 0)
  dt.setDate(dt.getDate() + n)
  return dt
}

/**
 * `n` days after a `YYYY-MM-DD` calendar day, as `YYYY-MM-DD`.
 *
 * Parses through local midnight rather than `new Date('2026-09-19')`, which the language parses as
 * UTC midnight and so lands on the 18th everywhere west of Greenwich.
 */
export const shiftISODate = (iso, n) => {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso))
  if (!m) return ''
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  dt.setDate(dt.getDate() + n)
  return toLocalISODate(dt)
}

/**
 * Parse a date string that is known to be UTC.
 * Appends "Z" when the string has no timezone indicator so
 * `new Date()` treats it as UTC rather than local time.
 */
export const parseUTC = (dateStr) => {
  if (!dateStr) return new Date(NaN)
  const s = String(dateStr).trim()
  // Already has a timezone indicator (Z, +HH:MM, -HH:MM)
  if (/Z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
    return new Date(s)
  }
  return new Date(s + 'Z')
}

/**
 * Format a UTC date string as a local time string.
 * e.g. "4/24/2026, 10:15:56 AM"
 */
export const formatLocalDateTime = (dateStr) => {
  const d = parseUTC(dateStr)
  if (isNaN(d)) return ''
  return d.toLocaleString()
}

/**
 * Format a UTC date string as a local date only.
 * e.g. "04/24/2026"
 */
export const formatLocalDate = (dateStr, opts) => {
  const d = parseUTC(dateStr)
  if (isNaN(d)) return ''
  return d.toLocaleDateString('en-US', opts || { month: '2-digit', day: '2-digit', year: 'numeric' })
}

/**
 * Relative "time ago" string from a UTC date.
 */
export const timeAgo = (dateStr) => {
  const now = new Date()
  const date = parseUTC(dateStr)
  if (isNaN(date)) return ''
  const seconds = Math.floor((now - date) / 1000)
  if (seconds < 0) return 'just now'        // future dates (clock skew)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
